/**
 * LaunchMonitorImportReview
 *
 * Modal component for importing launch monitor data from image or CSV.
 *
 * Flow:
 *   1. User picks import type (Image OCR or CSV)
 *   2. User selects optional device brand
 *   3. User uploads file
 *   4. Backend parses → extracted fields appear in a review table
 *   5. User can edit any field before confirming
 *   6. onConfirm is called with the final field values (keyed by form field names)
 *
 * Field name mapping:
 *   - API returns aggregator suffixes: _driver, _7i, _wedge1
 *   - The form for wedge uses _wedge (not _wedge1) — adapted in adaptForForm()
 */

import React, { useState, useRef } from 'react';
import {
  parseLaunchMonitorImage,
  parseLaunchMonitorCSV,
  ParsedLaunchMonitorData,
  MonitorBrand,
} from '../services/launchMonitorImportService';
import { logEvent } from '../services/telemetryService';
// --- C2/C4 shot persistence (additive; see ShotConditionsFields.tsx) ---------
import ShotConditionsFields, {
  emptyConditions,
  conditionsError,
} from './launchMonitor/ShotConditionsFields';
import {
  buildShotFields,
  saveLaunchMonitorShot,
  ShotConditions,
} from '../services/launchMonitorShotService';
// ---------------------------------------------------------------------------
import LaunchMonitorCrossCheck from './LaunchMonitorCrossCheck';

// X7 bay mode: attaches the shot to a specific build (or the golfer's current
// club, for the baseline) instead of leaving it unlinked. Optional — every
// other caller of this modal (advanced profile form, post-fitting review)
// omits it and shots land exactly as they did before X7.
export interface BuildContext {
  profileId: number;
  headId: number | null;
  shaftId: number | null;
  shaftFlex: string | null;
  isBaseline: boolean;
  fittingId: string;
  /** Shown in the modal header so the fitter knows which build this capture is for. */
  buildLabel: string;
}

interface Props {
  clubType: 'driver' | 'iron' | 'wedge';
  onConfirm: (fields: Record<string, number | undefined>) => void;
  onClose: () => void;
  buildContext?: BuildContext;
}

type ImportMode = 'image' | 'csv';

const BRAND_OPTIONS: { value: MonitorBrand | ''; label: string }[] = [
  { value: '', label: 'Auto-detect' },
  { value: 'trackman', label: 'TrackMan' },
  { value: 'gcquad', label: 'GCQuad / GC3 (Foresight)' },
  { value: 'mevo_plus', label: 'Mevo+ (FlightScope)' },
  { value: 'garmin', label: 'Garmin Golf' },
  { value: 'skytrak', label: 'SkyTrak' },
];

// Human-readable labels for field base names
const FIELD_LABELS: Record<string, string> = {
  launch_angle: 'Launch Angle',
  spin_rate: 'Spin Rate',
  ball_speed: 'Ball Speed',
  carry_distance: 'Carry Distance',
  club_head_speed: 'Club Head Speed',
  smash_factor: 'Smash Factor',
  face_to_path: 'Face to Path',
  spin_axis: 'Spin Axis',
  peak_height: 'Peak Height',
  descent_angle: 'Descent Angle',
  dynamic_loft: 'Dynamic Loft',
  club_path: 'Club Path',
  face_angle: 'Face Angle',
  attack_angle: 'Attack Angle',
  side_deviation: 'Side Deviation',
  launch_angle_std_dev: 'Launch Angle Std Dev',
  spin_rate_std_dev: 'Spin Rate Std Dev',
  ball_speed_std_dev: 'Ball Speed Std Dev',
  shot_count: 'Shot Count',
};

// Derive a human label from a full field name like "launch_angle_driver"
function fieldLabel(fullName: string): string {
  const suffixes = ['_driver', '_7i', '_wedge1', '_wedge'];
  const prefixes = ['driver_', 'iron_', 'wedge_'];

  let base = fullName;
  for (const s of suffixes) {
    if (base.endsWith(s)) { base = base.slice(0, -s.length); break; }
  }
  for (const p of prefixes) {
    if (base.startsWith(p)) { base = base.slice(p.length); break; }
  }
  return FIELD_LABELS[base] ?? base.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// F6: human-readable label for auto_correct_value()'s correction_rule
const CORRECTION_RULE_LABELS: Record<string, string> = {
  decimal: 'decimal point inserted',
  digit_swap: 'digit misread',
  sign: 'sign flipped',
};

// Confidence → colour
function confidenceBadge(confidence: number): string {
  if (confidence >= 0.9) return 'bg-green/15 text-green-ink';
  if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
}
function confidenceLabel(confidence: number, method: string): string {
  if (method === 'csv') return 'CSV';
  if (confidence >= 0.9) return 'High';
  if (confidence >= 0.6) return 'Med';
  return 'Low';
}

/**
 * The API returns aggregator suffixes (_driver, _7i, _wedge1).
 * The form uses _wedge (not _wedge1) for wedge club type.
 * Also the aggregator names carry distance as {club}_carry_distance
 * which already matches the form field names.
 */
function adaptForForm(
  fields: ParsedLaunchMonitorData['fields'],
  clubType: 'driver' | 'iron' | 'wedge',
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, fieldData] of Object.entries(fields)) {
    let formKey = key;
    if (clubType === 'wedge') {
      formKey = formKey.replace(/_wedge1$/, '_wedge');
    }
    out[formKey] = fieldData.value;
  }
  return out;
}

/**
 * Same key adaptation as adaptForForm, but keeping confidence and the original
 * label so a user edit can be recorded as a correction against what was read.
 */
function adaptParsedForDiff(
  fields: ParsedLaunchMonitorData['fields'],
  clubType: 'driver' | 'iron' | 'wedge',
): Record<string, { value: number; confidence?: number; source_label?: string }> {
  const out: Record<string, { value: number; confidence?: number; source_label?: string }> = {};
  for (const [key, fieldData] of Object.entries(fields)) {
    let formKey = key;
    if (clubType === 'wedge') {
      formKey = formKey.replace(/_wedge1$/, '_wedge');
    }
    out[formKey] = {
      value: fieldData.value,
      confidence: fieldData.confidence,
      source_label: fieldData.source_label,
    };
  }
  return out;
}

const LaunchMonitorImportReview: React.FC<Props> = ({ clubType, onConfirm, onClose, buildContext }) => {
  const [mode, setMode] = useState<ImportMode>('csv');
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const [brand, setBrand] = useState<MonitorBrand | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ParsedLaunchMonitorData | null>(null);
  // Editable field values (initialized from API response, user can override)
  const [editedFields, setEditedFields] = useState<Record<string, string>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- C2/C4 shot persistence state (additive) ------------------------------
  const [conditions, setConditions] = useState<ShotConditions>(emptyConditions());
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // --------------------------------------------------------------------------

  const clubLabel = clubType === 'driver' ? 'Driver' : clubType === 'iron' ? '7-Iron' : 'Wedge';

  // The API keys wedge fields _wedge1; the form uses _wedge. Same rule as
  // adaptForForm, kept here so the cross-check panel writes to the row the
  // user is actually looking at.
  const toFormKey = (apiKey: string): string =>
    clubType === 'wedge' ? apiKey.replace(/_wedge1$/, '_wedge') : apiKey;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let parsed: ParsedLaunchMonitorData;
      const brandArg = brand || undefined;

      if (mode === 'csv') {
        parsed = await parseLaunchMonitorCSV(file, clubType, brandArg as MonitorBrand | undefined);
      } else {
        parsed = await parseLaunchMonitorImage(file, clubType, brandArg as MonitorBrand | undefined);
      }

      setResult(parsed);
      logEvent({
        session_id: sessionIdRef.current,
        event_type: 'upload_attempted',
        metadata: { method: mode, success: true, club_type: clubType },
      });

      // Initialise editable values from adapted field names
      const adapted = adaptForForm(parsed.fields, clubType);
      const initial: Record<string, string> = {};
      for (const [key, val] of Object.entries(adapted)) {
        initial[key] = String(val);
      }
      setEditedFields(initial);

    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Upload failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      logEvent({
        session_id: sessionIdRef.current,
        event_type: 'upload_attempted',
        metadata: { method: mode, success: false, club_type: clubType },
      });
    } finally {
      setLoading(false);
      // Reset file input so same file can be re-uploaded
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const collectFinalFields = (): Record<string, number | undefined> => {
    const finalFields: Record<string, number | undefined> = {};
    for (const [key, strVal] of Object.entries(editedFields)) {
      const num = parseFloat(strVal);
      finalFields[key] = isNaN(num) ? undefined : num;
    }
    return finalFields;
  };

  // --- C2/C4: persist the shot, then apply to the form ----------------------
  // The aggregate form was previously the only thing that survived review, so
  // the per-shot numbers were lost. Now the shot is stored first, with the
  // user's own corrections recorded against the parser's original reading.
  const handleConfirm = async () => {
    const finalFields = collectFinalFields();
    const invalid = conditionsError(conditions);
    if (invalid) {
      setSaveError(invalid);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const parsedForDiff = result ? adaptParsedForDiff(result.fields, clubType) : {};
      await saveLaunchMonitorShot({
        club_type: clubType,
        fields: buildShotFields(finalFields, parsedForDiff),
        conditions,
        session_id: sessionIdRef.current,
        club_label: clubLabel,
        source: mode === 'csv' ? 'csv' : 'ocr',
        extraction_method: result?.extraction_method,
        device_template: result?.template_matched ?? undefined,
        ocr_confidence: result?.template_confidence,
        ...(buildContext ? {
          profile_id: buildContext.profileId,
          head_id: buildContext.headId ?? undefined,
          shaft_id: buildContext.shaftId ?? undefined,
          shaft_flex: buildContext.shaftFlex ?? undefined,
          is_baseline: buildContext.isBaseline,
          fitting_id: buildContext.fittingId,
        } : {}),
      });
      onConfirm(finalFields);
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Could not save shot';
      setSaveError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  // Escape hatch: a failed save must not trap the user's reviewed values in the
  // modal. Applying without saving is allowed, but never silently.
  const handleApplyWithoutSaving = () => onConfirm(collectFinalFields());
  // --------------------------------------------------------------------------

  const fieldEntries = result
    ? Object.entries(adaptForForm(result.fields, clubType))
    : [];

  // Sort: primary fields first, std_dev and shot_count last
  fieldEntries.sort(([a], [b]) => {
    const isSecondary = (k: string) => k.includes('std_dev') || k.includes('shot_count');
    if (isSecondary(a) && !isSecondary(b)) return 1;
    if (!isSecondary(a) && isSecondary(b)) return -1;
    return a.localeCompare(b);
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Import Launch Monitor Data
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {clubLabel} data{buildContext ? ` — ${buildContext.buildLabel}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">

          {/* Mode toggle */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Import type</p>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden w-fit">
              {(['csv', 'image'] as ImportMode[]).map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setMode(m); setResult(null); setError(null); }}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    mode === m
                      ? 'bg-green-ink text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {m === 'csv' ? 'CSV Export' : 'Screenshot / Photo'}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {mode === 'csv'
                ? 'Upload a CSV or TSV export file from your launch monitor software'
                : 'Upload a screenshot or photo of your launch monitor display'}
            </p>
          </div>

          {/* Brand selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Device brand <span className="text-gray-400 font-normal">(optional — improves accuracy)</span>
            </label>
            <select
              value={brand}
              onChange={e => setBrand(e.target.value as MonitorBrand | '')}
              className="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink px-3 py-2"
            >
              {BRAND_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* File upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {mode === 'csv' ? 'CSV / TSV file' : 'Image file'}
            </label>
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-green-ink hover:bg-green/10 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="text-3xl mb-2">
                {mode === 'csv' ? '📄' : '🖼️'}
              </div>
              <p className="text-sm text-gray-600">
                {loading
                  ? 'Uploading...'
                  : mode === 'csv'
                    ? 'Click to upload CSV/TSV file'
                    : 'Click to upload screenshot or photo'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {mode === 'csv' ? '.csv, .tsv, .txt (max 5MB)' : 'JPEG, PNG, WebP (max 10MB)'}
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={mode === 'csv' ? '.csv,.tsv,.txt,text/csv,text/tab-separated-values' : 'image/*'}
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center gap-3 text-gray-600">
              <div className="animate-spin w-5 h-5 border-2 border-green-ink border-t-transparent rounded-full" />
              <span className="text-sm">
                {mode === 'csv' ? 'Parsing CSV...' : 'Running OCR — this may take a few seconds...'}
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm font-medium text-red-800">Import failed</p>
              <p className="text-xs text-red-700 mt-1">{error}</p>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-4">
              {/* Summary banner */}
              <div className={`rounded-lg p-3 flex items-center gap-3 ${
                result.success ? 'bg-green/10 border border-green-ink' : 'bg-amber-50 border border-amber-200'
              }`}>
                <span className="text-xl">{result.success ? '✅' : '⚠️'}</span>
                <div>
                  <p className="text-sm font-medium">
                    {result.success
                      ? `Extracted ${fieldEntries.length} fields from ${result.shot_count} shot${result.shot_count !== 1 ? 's' : ''}`
                      : 'Extraction had issues'}
                  </p>
                  {result.template_matched && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      Device: {result.template_matched} · Method: {result.extraction_method}
                    </p>
                  )}
                </div>
              </div>

              {/* Warnings */}
              {result.warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Notes</p>
                  <ul className="space-y-0.5">
                    {result.warnings.map((w, i) => (
                      <li key={i} className="text-xs text-amber-700">• {w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* LLM fallback suggestion */}
              {result.needs_llm_fallback && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Low confidence extraction.</strong> Consider manually reviewing all values below,
                    or try re-uploading a higher-resolution image.
                  </p>
                </div>
              )}

              {/* Haiku cross-check — both reads shown, the user picks */}
              <LaunchMonitorCrossCheck
                status={result.llm_crosscheck_status}
                disagreements={result.field_disagreements}
                labelFor={fieldLabel}
                currentValue={apiKey => {
                  const raw = editedFields[toFormKey(apiKey)];
                  const parsed = raw === undefined ? NaN : parseFloat(raw);
                  return Number.isFinite(parsed) ? parsed : null;
                }}
                onUseValue={(apiKey, value) => setEditedFields(prev => ({
                  ...prev,
                  [toFormKey(apiKey)]: String(value),
                }))}
              />

              {/* Field review table */}
              {fieldEntries.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Review extracted values — edit anything that looks wrong:
                  </p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 w-2/5">Field</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 w-1/5">Source</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 w-1/5">Confidence</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 w-1/5">Value</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {fieldEntries.map(([formKey, _val]) => {
                          // Look up original API key for confidence/source
                          // For wedge: formKey uses _wedge, original uses _wedge1
                          const apiKey = clubType === 'wedge'
                            ? formKey.replace(/_wedge$/, '_wedge1').replace(/_wedge([^1])/, '_wedge1$1')
                            : formKey;
                          const fieldData = result.fields[apiKey] ?? result.fields[formKey];
                          const conf = fieldData?.confidence ?? 1.0;
                          const sourceLabel = fieldData?.source_label ?? '';
                          const correctedFrom = fieldData?.original_value;
                          const correctionRule = fieldData?.correction_rule
                            ? CORRECTION_RULE_LABELS[fieldData.correction_rule] ?? fieldData.correction_rule
                            : '';

                          return (
                            <tr key={formKey} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium text-gray-800">
                                {fieldLabel(formKey)}
                                {correctedFrom != null && (
                                  <span
                                    title={`OCR originally read ${correctedFrom} — auto-corrected (${correctionRule})`}
                                    className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800 align-middle"
                                  >
                                    corrected from {correctedFrom}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-gray-500 text-xs truncate max-w-[120px]" title={sourceLabel}>
                                {sourceLabel}
                              </td>
                              <td className="px-3 py-2">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${confidenceBadge(conf)}`}>
                                  {confidenceLabel(conf, result.extraction_method)}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  step="any"
                                  value={editedFields[formKey] ?? ''}
                                  onChange={e => setEditedFields(prev => ({
                                    ...prev,
                                    [formKey]: e.target.value,
                                  }))}
                                  className="w-full text-sm border rounded px-2 py-1 focus:ring-1 focus:ring-green-ink focus:border-green-ink"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* --- C2/C4 session conditions (additive block) ------------- */}
              <ShotConditionsFields
                value={conditions}
                onChange={setConditions}
                suggestedBrand={brand || undefined}
              />
              {/* ---------------------------------------------------------- */}

              {/* Unmapped fields info */}
              {Object.keys(result.unmapped_fields).length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-600 mb-1">
                    Unrecognized columns (not imported):
                  </p>
                  <p className="text-xs text-gray-500">
                    {Object.keys(result.unmapped_fields).join(', ')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          {/* --- C2/C4: save failure must not trap the reviewed values ----- */}
          {saveError && (
            <div className="flex items-center gap-3 mr-auto">
              <span className="text-xs text-red-600 max-w-xs">{saveError}</span>
              <button
                type="button"
                onClick={handleApplyWithoutSaving}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Apply without saving shot
              </button>
            </div>
          )}
          {/* -------------------------------------------------------------- */}
          {result && fieldEntries.length > 0 && (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-green-ink rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : `Save & Apply (${fieldEntries.length} fields)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LaunchMonitorImportReview;
